import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useParams,
  Navigate,
} from "react-router-dom";
import DisplayController from "./components/core/DisplayController";
import ErrorBoundary from "./components/shared/ErrorBoundary";
import AuthScreen from "./components/Auth/AuthScreen";
import PreviewPage from "./pages/PreviewPage";
import MonthPage from "./pages/MonthPage";
import HomeShell from "./components/home/HomeShell";
import { isHomeCode } from "./utils/homeScreen";
import { isAuthenticated } from "./services/authService";
import { performStartupMaintenance } from "./utils/assetMaintenance";
import { applyTVClasses } from "./utils/deviceDetection";
import {
  checkServiceWorkerDomainMatch,
  clearAllServiceWorkerData,
} from "./utils/serviceWorkerUtils";
// Import or define runtimeConfig
import { runtimeConfig } from "./config/runtime";

// Azure Best Practice: Setup service worker communication at application root
function App() {
  // Azure Best Practice: Add service worker registration diagnostic code
  useEffect(() => {
    // Check if we're in production
    if (runtimeConfig.isProduction) {
      console.log("Running in production mode, checking service worker status");

      // Check if service worker is available
      if ("serviceWorker" in navigator) {
        console.log("Service Worker API is available");

        // Check if any service workers are registered
        navigator.serviceWorker
          .getRegistrations()
          .then(async (registrations) => {
            console.log(
              `Found ${registrations.length} registered service workers:`,
              registrations
            );

            registrations.forEach((reg) => {
              console.log(
                `SW scope: ${reg.scope}, state: ${reg.active?.state || "none"}`
              );
            });

            // Check for domain mismatch (CORS issue)
            const domainMatch = await checkServiceWorkerDomainMatch();
            if (!domainMatch) {
              console.warn(
                "Service Worker domain mismatch detected - this may cause CORS issues"
              );
              console.log(
                "Consider clearing service worker data if experiencing issues"
              );

              // Optionally auto-clear if there's a clear mismatch and no active state
              const hasInactiveRegistrations = registrations.some(
                (reg) =>
                  reg.active?.state !== "activated" &&
                  reg.scope !== window.location.origin + "/"
              );

              if (hasInactiveRegistrations) {
                console.log(
                  "Auto-clearing problematic service worker registrations..."
                );
                await clearAllServiceWorkerData();
                // Don't reload here as it would cause an infinite loop
              }
            }
          });

        // Listen for controller changes
        navigator.serviceWorker.addEventListener("controllerchange", () => {
          console.log("Service worker controller changed");
        });
      } else {
        console.warn("Service Worker API is not available in this browser");
      }
    }
  }, []);

  useEffect(() => {
    // Register service worker message handler at the application level
    if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
      console.log("Setting up service worker message handler");

      // Create message handler for prayer time updates
      const messageHandler = (event: MessageEvent) => {
        if (event.data && event.data.type === "PRAYER_TIMES_UPDATED") {
          console.log(
            "Received prayer time update from service worker:",
            event.data
          );

          // Dispatch a custom event that components can listen for
          window.dispatchEvent(
            new CustomEvent("prayerTimesUpdated", {
              detail: event.data,
            })
          );
        }
      };

      // Add the message listener
      navigator.serviceWorker.addEventListener("message", messageHandler);

      // Cleanup when app unmounts
      return () => {
        navigator.serviceWorker.removeEventListener("message", messageHandler);
      };
    }
  }, []);

  // Perform startup maintenance
  useEffect(() => {
    performStartupMaintenance();

    // Protect caches/IndexedDB from browser eviction — kiosks must survive
    // offline on yesterday's data
    if (navigator.storage?.persist) {
      navigator.storage.persist().catch(() => {});
    }

    // One-time cleanup: the v1 cache held opaque (no-cors) entries that the
    // v2 crossOrigin requests can't consume
    if ("caches" in window) {
      caches.delete("digital-signage-cache").catch(() => {});
    }
  }, []);

  useEffect(() => {
    applyTVClasses();
  }, []);

  return (
    <ErrorBoundary>
      <Router>
        <Routes>
          {/* Authentication route */}
          <Route path="/" element={<AuthScreen />} />

          {/* Protected display route with screen code parameter */}
          <Route
            path="/display/:screenCode"
            element={
              <ProtectedRoute>
                <DisplayControllerWrapper />
              </ProtectedRoute>
            }
          />

          {/* Colour preview framed by the admin screen page; no screen login */}
          <Route path="/preview" element={<PreviewPage />} />

          {/* A masjid's monthly timetable, no screen login */}
          <Route path="/month" element={<MonthPage />} />

          {/* Masjid 24/7 Home: the household tablet (also what the Android app opens) */}
          <Route path="/home" element={<HomeShell />} />

          {/* Catch all other routes and redirect to home/auth */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Router>
    </ErrorBoundary>
  );
}

// Protected route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return isAuthenticated() ? <>{children}</> : <Navigate to="/" replace />;
};

// Wrapper component to extract URL parameters
function DisplayControllerWrapper() {
  const { screenCode } = useParams<{ screenCode: string }>();

  if (!screenCode) {
    return <div>Screen code is required</div>;
  }

  if (isHomeCode(screenCode)) {
    return <Navigate to="/home" replace />;
  }

  return <DisplayController screenCode={screenCode} />;
}

export default App;
